import React from "react";
import ToggleField from "@/shared/components/ui/Form/ToggleField";
import {
  CONTENT_GENERATION,
  CONTENT_GENERATION_COPY,
  nextContentGeneration,
  validateMessageTemplates,
} from "@/features/campaigns/utils/stepContentMode";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { ArrowRight, Mail } from "lucide-react";
import OnboardingSectionCard from "@/features/onboarding/components/OnboardingSectionCard";
import CampaignMessageTemplates from "./Messaging/CampaignMessageTemplates";
import usePreviewLeads from "./Messaging/usePreviewLeads";
import CampaignForm from "./Form/CampaignForm";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import DefinePitch from "@/features/onboarding/components/Onboarding/DefinePitch";
import DefineAudienceForm from "@/features/onboarding/components/Onboarding/Tailored/DefineAudienceForm";
import CampaignModalWizardShell from "./CampaignModalWizardShell";
import CampaignAiTrainingStep from "./CampaignAiTrainingStep";
import CampaignMessageStyleStep from "./CampaignMessageStyleStep";
import { useNewCampaignWizard } from "./useNewCampaignWizard";

const NewCampaignModal = ({
  isOpen,
  onClose,
  campaignId = null,
  width = "1130px",
}) => {
  const w = useNewCampaignWizard({ isOpen, onClose, campaignId });
  const { leads: previewLeads, sender: previewSender } = usePreviewLeads(campaignId);

  if (w.isEditMode && w.isLoadingCampaign) {
    return (
      <CustomModal isOpen={isOpen} onClose={onClose} width="950px">
        <div className="flex items-center justify-center py-20">
          <Loader text="Loading campaign data…" />
        </div>
      </CustomModal>
    );
  }

  const nextStepLabel = w.wizardSteps[w.wizardStep + 1]?.label;

  const footer = (
    <div className="flex items-center justify-between gap-3">
      {w.wizardStep === 0 ? (
        <>
          <SecondaryButton
            title="Cancel"
            className="text-gray-500 shadow-none"
            onClick={onClose}
            disabled={w.isSavingCampaign}
          />
          <div className="flex items-center gap-3">
            {nextStepLabel ? (
              <p className="text-[0.71rem] font-normal text-gray-500">
                Next: {nextStepLabel}
              </p>
            ) : null}
            <PrimaryButton
              onClick={w.handleStep0Continue}
              // disabled={w.isSavingCampaign}
              startContent={false}
              endContent={<ArrowRight size={16} className="text-white" />}
            >
              Continue
            </PrimaryButton>
          </div>
        </>
      ) : w.wizardStep === 1 ? (
        <>
          <SecondaryButton
            title="Back"
            className="text-gray-500 shadow-none"
            onClick={() => {
              w.clearTopFieldErrors();
              w.setWizardStep(0);
            }}
            disabled={w.isSavingCampaign}
          />
          <div className="flex items-center gap-3">
            {nextStepLabel ? (
              <p className="text-[0.71rem] font-normal text-gray-500">
                Next: {nextStepLabel}
              </p>
            ) : null}
            <PrimaryButton
              onClick={w.handleStep1Continue}
              disabled={w.isSavingCampaign}
              startContent={false}
              endContent={<ArrowRight size={16} className="text-white" />}
            >
              Continue
            </PrimaryButton>
          </div>
        </>
      ) : w.wizardStep === 2 ? (
        <>
          <SecondaryButton
            title="Back"
            className="text-gray-500 shadow-none"
            onClick={() => {
              w.clearTopFieldErrors();
              w.setWizardStep(1);
            }}
            disabled={w.isSavingCampaign}
          />
          <div className="flex items-center gap-3">
            {nextStepLabel ? (
              <p className="text-[0.71rem] font-normal text-gray-500">
                Next: {nextStepLabel}
              </p>
            ) : null}
            <PrimaryButton
              onClick={w.handleAudienceContinueToMessaging}
              disabled={w.isSavingCampaign}
              startContent={false}
              endContent={<ArrowRight size={16} className="text-white" />}
            >
              Continue
            </PrimaryButton>
          </div>
        </>
      ) : w.wizardStep === 3 ? (
        <>
          <SecondaryButton
            title="Back"
            className="text-gray-500 shadow-none"
            onClick={() => {
              w.clearTopFieldErrors();
              w.setWizardStep(2);
            }}
            disabled={w.isSavingCampaign}
          />
          <div className="flex items-center gap-3">
            {nextStepLabel ? (
              <p className="text-[0.71rem] font-normal text-gray-500">
                Next (optional): {nextStepLabel}
              </p>
            ) : null}
            <PrimaryButton
              onClick={w.handleMessagingContinueToTraining}
              disabled={w.isSavingCampaign || w.messageStyle.length === 0}
              isLoading={w.isSavingCampaign}
              startContent={false}
              endContent={<ArrowRight size={16} className="text-white" />}
            >
              {w.isEditMode
                ? "Save changes & continue"
                : "Create campaign & continue"}
            </PrimaryButton>
          </div>
        </>
      ) : (
        <>
          <SecondaryButton
            title="Back"
            className="text-gray-500 shadow-none"
            onClick={() => w.setWizardStep(3)}
            disabled={w.isSavingCampaign}
          />
          <PrimaryButton
            onClick={w.handleSkipAiTrainingStep}
            disabled={w.isSavingCampaign}
            startContent={false}
          >
            Finish
          </PrimaryButton>
        </>
      )}
    </div>
  );

  const stepBody =
    w.wizardStep === 0 ? (
      <CampaignForm
        detailsOnly
        pitchData={w.pitchData}
        audienceData={w.audienceData}
        showDefineSections={false}
        onFormDataChange={w.handleFormDataChange}
        isEditMode={w.isEditMode}
        initialValues={w.formData}
        onFormValidationRef={w.formValidationRef}
        validationErrors={w.validationErrors}
        onCancel={onClose}
      />
    ) : w.wizardStep === 1 ? (
      <DefinePitch
        key={`pitch-${campaignId ?? "new"}`}
        registerSubmitHandler={w.registerPitchSubmitHandler}
        onDataChange={w.handlePitchDataChange}
        initialCompanyAssets={w.companyAssets || []}
        initialValues={w.pitchData}
        isEditMode={w.isEditMode}
        suppressUnsavedWarning
      />
    ) : w.wizardStep === 2 ? (
      <DefineAudienceForm
        key={`audience-${campaignId ?? "new"}`}
        registerSubmitHandler={w.registerAudienceSubmitHandler}
        onDataChange={w.handleAudienceDataChange}
        initialValues={w.audienceData}
        suppressUnsavedWarning
      />
    ) : w.wizardStep === 3 ? (
      <div className="flex flex-col gap-4">
        <CampaignMessageStyleStep
          value={w.messageStyle}
          onChange={w.handleMessageStyleChange}
          error={w.validationErrors.messageStyleError}
        />
        <ToggleField
          label="AI-personalized content"
          description={CONTENT_GENERATION_COPY[w.contentGeneration]}
          checked={w.contentGeneration !== CONTENT_GENERATION.OFF}
          onChange={(aiOn) => w.setContentGeneration(nextContentGeneration(aiOn))}
        />
        {/* Campaign messages are only for campaigns without AI-written content. */}
        {w.contentGeneration === CONTENT_GENERATION.OFF && (
        <OnboardingSectionCard
          icon={<Mail className="text-gray-500" size={20} />}
          title="Campaign messages"
          description={
            w.contentGeneration === CONTENT_GENERATION.OFF
              ? "Every step of a channel sends its message, unless the step has its own. Required for channels your steps use."
              : "Optional. Sent for any step AI hasn't written yet, so leads never wait on generation."
          }
        >
          <CampaignMessageTemplates
            value={w.messageTemplates}
            onChange={w.setMessageTemplates}
            errors={validateMessageTemplates(w.messageTemplates)}
            previewLeads={previewLeads}
            sender={previewSender}
          />
        </OnboardingSectionCard>
        )}
      </div>
    ) : (
      <CampaignAiTrainingStep onTrainerClick={w.handleTrainerLinkClick} />
    );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      width={width}
      className="p-0 h-[calc(100dvh-100px)]"
      overlayClassName="z-[100000001]"
    >
      <CampaignModalWizardShell
        steps={w.wizardSteps}
        activeStep={w.wizardStep}
        progressValue={w.progressValue}
        headerTitle={w.currentStepMeta.title}
        headerDescription={w.currentStepMeta.description}
        onClose={onClose}
        footer={footer}
        isEditMode={w.isEditMode}
        topFieldErrors={w.topFieldErrors}
      >
        {stepBody}
      </CampaignModalWizardShell>
    </CustomModal>
  );
};

export default NewCampaignModal;
